/**
 * Type definitions for EthPending API
 */

/**
 * A log entry from a transaction
 */
export interface TransactionLog {
  /** Contract address that emitted the log */
  address: string;
  /** Array of indexed log arguments (topics) */
  topics: string[];
  /** Non-indexed log data */
  data: string;
  /** Block number (null for pending transactions) */
  blockNumber: number | null;
  /** Transaction hash */
  transactionHash: string;
  /** Transaction index in the block */
  transactionIndex: number | null;
  /** Block hash */
  blockHash: string | null;
  /** Log index in the block */
  logIndex: number | null;
  /** Whether the log was removed (due to chain reorganization) */
  removed: boolean;
}

/**
 * A pending transaction from the mempool
 */
export interface PendingTransaction {
  /** Transaction hash */
  txHash: string;
  /** Whether the transaction was successful (true) or failed (false) */
  success: boolean;
  /** Transaction logs (events) */
  logs: TransactionLog[];
  /** Timestamp when the transaction was received (ISO 8601) */
  timestamp: string;
  /** Network identifier (e.g., "mainnet", "sepolia") */
  network: string;
  /** Error message if the transaction failed */
  error?: string;
  /** Transaction sender address */
  from?: string;
  /** Transaction recipient address */
  to?: string;
  /** Transaction value in wei */
  value?: string;
  /** Gas price in wei */
  gasPrice?: string;
  /** Gas limit */
  gas?: string;
  /** Transaction input data */
  input?: string;
  /** Transaction nonce */
  nonce?: number;
}

/**
 * WebSocket connected message
 */
export interface ConnectedMessage {
  type: 'connected';
  message: string;
}

/**
 * WebSocket subscribed message
 */
export interface SubscribedMessage {
  type: 'subscribed';
  filters: {
    addresses?: string[];
    methods?: string[];
  };
}

/**
 * WebSocket pending transaction message
 */
export interface PendingTxMessage {
  type: 'pending_tx';
  data: PendingTransaction;
}

/**
 * WebSocket error message
 */
export interface ErrorMessage {
  type: 'error';
  message: string;
  code?: string;
}

/**
 * All possible WebSocket message types
 */
export type WebSocketMessage = ConnectedMessage | SubscribedMessage | PendingTxMessage | ErrorMessage;

/**
 * Filter options for transactions
 */
export interface FilterOptions {
  /** Array of contract addresses to filter by */
  addresses?: string[];
  /** Array of method signatures (first 4 bytes) to filter by */
  methods?: string[];
}

/**
 * HTTP API response
 */
export interface HTTPResponse {
  /** Whether the request was successful */
  success: boolean;
  /** Array of pending transactions */
  transactions: PendingTransaction[];
  /** Total number of transactions available */
  total?: number;
  /** Error message if the request failed */
  error?: string;
}

/**
 * Client configuration options
 */
export interface ClientConfig {
  /** API key for authentication */
  apiKey: string;
  /** WebSocket URL (defaults to wss://api.ethpending.com/ws) */
  wsUrl?: string;
  /** HTTP API URL (defaults to https://api.ethpending.com) */
  httpUrl?: string;
  /** Whether to automatically reconnect on disconnect (default: true) */
  autoReconnect?: boolean;
  /** Maximum number of reconnection attempts (default: Infinity) */
  maxReconnectAttempts?: number;
  /** Initial reconnection delay in milliseconds (default: 1000) */
  reconnectDelay?: number;
  /** Maximum reconnection delay in milliseconds (default: 60000) */
  maxReconnectDelay?: number;
  /** Heartbeat interval in milliseconds (default: 30000) */
  heartbeatInterval?: number;
  /** Connection timeout in milliseconds (default: 10000) */
  connectionTimeout?: number;
}

/**
 * Connection state
 */
export enum ConnectionState {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  DISCONNECTING = 'disconnecting',
}

/**
 * Metrics for tracking connection and message statistics
 */
export interface Metrics {
  /** Number of messages received */
  messagesReceived: number;
  /** Number of messages sent */
  messagesSent: number;
  /** Connection uptime in milliseconds */
  uptime: number;
  /** Number of reconnection attempts */
  reconnectAttempts: number;
  /** Total number of errors */
  errors: number;
  /** Error counts by type */
  errorsByType: Record<string, number>;
  /** Current connection state */
  connectionState: ConnectionState;
  /** Timestamp of last connection */
  lastConnectedAt?: number;
  /** Average latency in milliseconds (HTTP only) */
  averageLatency?: number;
}

/**
 * Retry options for exponential backoff
 */
export interface RetryOptions {
  /** Initial delay in milliseconds */
  initialDelay: number;
  /** Maximum delay in milliseconds */
  maxDelay: number;
  /** Maximum number of retry attempts */
  maxAttempts: number;
  /** Multiplier for exponential backoff (default: 2) */
  multiplier?: number;
  /** Whether to add jitter to prevent thundering herd (default: true) */
  jitter?: boolean;
}

/**
 * Error types
 */
export class EthPendingError extends Error {
  constructor(message: string, public code?: string) {
    super(message);
    this.name = 'EthPendingError';
  }
}

export class ConnectionError extends EthPendingError {
  constructor(message: string) {
    super(message, 'CONNECTION_ERROR');
    this.name = 'ConnectionError';
  }
}

export class ApiError extends EthPendingError {
  constructor(message: string, public statusCode?: number) {
    super(message, 'API_ERROR');
    this.name = 'ApiError';
  }
}

export class RateLimitError extends ApiError {
  constructor(message: string, public retryAfter?: number) {
    super(message, 429);
    this.name = 'RateLimitError';
    this.code = 'RATE_LIMIT_ERROR';
  }
}

export class AuthenticationError extends ApiError {
  constructor(message: string) {
    super(message, 401);
    this.name = 'AuthenticationError';
    this.code = 'AUTHENTICATION_ERROR';
  }
}

/**
 * Event types for WebSocket client
 */
export interface WebSocketClientEvents {
  connected: void;
  disconnected: { code?: number; reason?: string };
  reconnecting: number;
  transaction: PendingTransaction;
  error: Error;
  subscribed: FilterOptions;
  stateChange: { oldState: ConnectionState; newState: ConnectionState };
}

/**
 * Event types for HTTP client
 */
export interface HTTPClientEvents {
  error: Error;
  rateLimit: number;
}

